import { useCallback, useEffect, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  allUnselectedState,
  getSignalFiltersParams,
  getSignalFiltersState,
  type useSignalFiltersParams,
} from './';

export interface SignalFilterItems<T> {
  count?: number;
  label: string;
  selected: boolean;
  value: T;
}

export interface SignalFilter<T> {
  items: SignalFilterItems<T>[];
  open: boolean;
}

export interface SignalFilters {
  action: SignalFilter<string>;
  eventType: SignalFilter<string>;
  kind: SignalFilter<string>;
  tags: SignalFilter<string>;
}

export interface SignalPanel {
  open: boolean;
}

export interface SignalFiltersState {
  filters: SignalFilters;
  panel: SignalPanel;
}

export type SignalFiltersAction =
  | { type: 'CLEAR_FILTERS' }
  | { type: 'REINITIALIZE'; params: string }
  | ({ type: 'SET_FILTER'; keepPage?: boolean; open?: boolean } & {
      [K in keyof SignalFilters]: { field: K; items: SignalFilters[K]['items'] };
    }[keyof SignalFilters])
  | { type: 'TOGGLE_FILTER'; field: keyof SignalFilters }
  | { type: 'TOGGLE_PANEL' };

export const useSignalFiltersReducer = (
  params: ReturnType<typeof useSignalFiltersParams>,
  eventTypes: readonly string[] | undefined,
  tagNames: readonly string[] | undefined,
) => {
  const [_, setSearchParams] = useSearchParams();

  const reducer = useCallback(
    (state: SignalFiltersState, action: SignalFiltersAction): SignalFiltersState => {
      switch (action.type) {
        case 'CLEAR_FILTERS': {
          setSearchParams((params) => {
            Object.keys(state.filters).forEach((x) => params.delete(x));
            params.delete('search');
            params.set('page', '1');
            return params;
          });

          return allUnselectedState;
        }

        case 'REINITIALIZE': {
          const urlSearchParams = new URLSearchParams(action.params);
          const params = getSignalFiltersParams(urlSearchParams);
          setSearchParams(() => urlSearchParams);
          return getSignalFiltersState(params);
        }

        case 'SET_FILTER': {
          const {
            field,
            items,
          }: { field: keyof SignalFilters; items: SignalFilterItems<string>[] } = action;
          const { keepPage = false } = action;
          const selected = items.filter((x) => x.selected);
          setSearchParams((params) => {
            selected.length > 0
              ? params.set(field, JSON.stringify(selected.map((x) => x.value)))
              : params.delete(field);
            if (!keepPage) params.set('page', '1'); // reset pagination when filters change
            return params;
          });
          return {
            ...state,
            filters: {
              ...state.filters,
              [field]: {
                ...state.filters[field],
                ...(action.open != null && { open: action.open }),
                items,
              },
            },
          };
        }

        case 'TOGGLE_FILTER': {
          return {
            ...state,
            filters: {
              ...state.filters,
              [action.field]: {
                ...state.filters[action.field],
                open: !state.filters[action.field].open,
              },
            },
          };
        }

        case 'TOGGLE_PANEL': {
          return {
            ...state,
            panel: { ...state.panel, open: !state.panel.open },
          };
        }

        default: {
          return state;
        }
      }
    },
    [setSearchParams],
  );

  const [state, dispatch] = useReducer(reducer, getSignalFiltersState(params));

  useEffect(() => {
    if (!eventTypes) return;
    if (eventTypes.join() === state.filters.eventType.items.map((x) => x.value).join()) return;
    dispatch({
      type: 'SET_FILTER',
      field: 'eventType',
      items: eventTypes.map((x) => ({
        label: x,
        selected: params.eventType.includes(x),
        value: x,
      })),
      keepPage: true,
      open: params.eventType.length > 0,
    });
  }, [eventTypes, params.eventType, state.filters.eventType.items]);

  useEffect(() => {
    if (!tagNames) return;
    if (tagNames.join() === state.filters.tags.items.map((x) => x.value).join()) return;
    dispatch({
      type: 'SET_FILTER',
      field: 'tags',
      items: tagNames.map((x) => ({
        label: x,
        selected: params.tags.includes(x),
        value: x,
      })),
      keepPage: true,
      open: params.tags.length > 0,
    });
  }, [tagNames, params.tags, state.filters.tags.items]);

  return [state, dispatch] as const;
};
